'use client'
import React, { useState, useEffect, useCallback } from 'react'
import { AspectRatio, Box, Spinner, Flex } from '@chakra-ui/react'
import Image from 'next/image'
import { QuestionIcon } from '@/_assets/icon-components'
import useEmblaCarousel from 'embla-carousel-react'
import { Thumb } from './ThumbsButton'
import './style.css'

interface SliderProps {
  data: string[]
  borderRadius?: string
  maxHeight?: string
  aspectRatio?: number
  thumbHeight?: any
}

export default function MultipleSlider({
  data,
  borderRadius = '0',
  maxHeight = '280px',
  aspectRatio = 1,
  thumbHeight = '80px', // Add a default value
}: SliderProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({
    slidesToScroll: 1,
    align: 'center',
  })
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
    slidesToScroll: 1,
  })

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return
      emblaMainApi.scrollTo(index)
    },
    [emblaMainApi, emblaThumbsApi]
  )

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return
    const selected = emblaMainApi.selectedScrollSnap()
    setSelectedIndex(selected)
    emblaThumbsApi.scrollTo(selected)
  }, [emblaMainApi, emblaThumbsApi])

  useEffect(() => {
    if (!emblaMainApi) return
    onSelect()
    emblaMainApi.on('select', onSelect)
    emblaMainApi.on('reInit', onSelect)

    return () => {
      emblaMainApi.off('select', onSelect)
    }
  }, [emblaMainApi, onSelect])

  useEffect(() => {
    if (data?.length > 0) {
      setIsLoading(false)
    }
  }, [data])

  if (isLoading)
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        height={maxHeight}
      >
        <Spinner size='lg' color='var(--primary-default)' />
      </Box>
    )

  if (data.length === 0) return null

  return (
    <Flex flexDirection='column' width='100%' className='embla'>
      <Box className='embla__viewport' ref={emblaMainRef} width='100%'>
        <Flex className='embla__container'>
          {data.map((item, index) => (
            <Box
              key={index}
              className='embla__slide'
              flex='0 0 100%'
              minWidth='0'
            >
              <AspectRatio ratio={aspectRatio} maxH={maxHeight} width='100%'>
                {item ? (
                  <Box position='relative' width='100%' height='100%'>
                    <Image
                      src={
                        item?.includes(process.env.BASE_URL!)
                          ? item
                          : process.env.BASE_URL + item
                      }
                      priority
                      style={{
                        objectFit: 'cover',
                        borderRadius,
                      }}
                      alt={'banner'}
                      fill
                    />
                  </Box>
                ) : (
                  <Box>
                    <QuestionIcon />
                  </Box>
                )}
              </AspectRatio>
            </Box>
          ))}
        </Flex>
      </Box>

      {data.length > 1 && (
        <Box className='embla-thumbs' mt={2} width='100%' overflow='hidden'>
          <Box className='embla-thumbs__viewport' ref={emblaThumbsRef}>
            <Flex className='embla-thumbs__container' gap={2}>
              {data.map((item: string, index) => (
                <Thumb
                  item={item}
                  key={index}
                  thumbHeight={thumbHeight}
                  onClick={() => onThumbClick(index)}
                  selected={index === selectedIndex}
                  index={index}
                />
              ))}
            </Flex>
          </Box>
        </Box>
      )}
    </Flex>
  )
}
